import { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async';
import { Button, CardActions, OutlinedInput, InputLabel, FormControl, CardContent, Divider, Card, CardHeader, Stack, MenuItem, Select, FormHelperText } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { useNavigate } from 'react-router-dom';
import { Agent, Business, Assignment, Company } from '../../../types/UserTypes';
import { SelectChangeEvent } from '@mui/material/Select';
import { BVTConstants } from '../../../../utils/BVTConstants';
import axios from '../../../../services/ApiService';
import { toast } from 'react-hot-toast';
import LoadingSpinner from '../../../support/LoadingSpinner';

function AdminNewUser() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState(false);
  const [programs, setPrograms] = useState<Map<number, string>>(new Map());
  const [workflows, setWorkflows] = useState<Map<number, string>>(new Map());
  const [businessData, setBusinessData] = useState<Business>({
    businessId: 0,
    userId: 0,
    firstName: '',
    lastName: '',
    displayName: '',
    email: '',
    contact: '',
    businessABN: '',
    legalName: '',
    tradeName: '',
    address: '',
    webLink: '',
    logoId: 0,
    documentId: 0,
    status: BVTConstants.ACTIVE,
    agentCompanyId: 0,
    agentCompanyName: '',
    assignments: [],
    serviceId: 0,
    sectorId: 0,
    businessType: '',
    businessSize: '',
    acnIcn: '',
    grossIncome: '',
    grossAssets: '',
    employees: '',
    supportRequired: [],
    createdAt: '',
    updatedAt: '',
    lastLoginAt: '',
  });
  const [assignment, setAssignment] = useState<Assignment>({
    id: 0,
    programId: 0,
    programName: '',
    workflowId: 0,
    workflowName: '',
    status: BVTConstants.ACTIVE,
    createdAt: '',
    updatedAt: '',
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const companiesResponse = await axios.post('/getCompanies', false);
        if (Array.isArray(companiesResponse.data)) {
          setCompanies(companiesResponse.data);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Error loading data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);
  
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setBusinessData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectChange = async (event: SelectChangeEvent<string>) => {
    const { name, value } = event.target;
    setBusinessData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    if (name === 'programId' && value) {
      setLoading(true);
      try {
        const workflowsResponse = await axios.get(`/getWorkflowsMap/${value}`);
        if (workflowsResponse.data) {
          const workflowMap = new Map(
            Object.entries(workflowsResponse.data).map(([key, value]) => [Number(key), String(value)])
          );
          setWorkflows(workflowMap);
        }
      } catch (error) {
        console.error('Error fetching workflows:', error);
        toast.error('Error loading workflows. Please try again.');
        setWorkflows(new Map());
      } finally {
        setLoading(false);
      }
    }
  };

  const handleProgramSelectChange = async (event: SelectChangeEvent<string>) => {
    const { name, value } = event.target;
    setAssignment(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    if (name === 'programId' && value) {
      setLoading(true);
      try {
        const workflowsResponse = await axios.get(`/getWorkflowsMap/${value}`);
        if (workflowsResponse.data) {
          const workflowMap = new Map(
            Object.entries(workflowsResponse.data).map(([key, value]) => [Number(key), String(value)])
          );
          setWorkflows(workflowMap);
        }
      } catch (error) {
        console.error('Error fetching workflows:', error);
        toast.error('Error loading workflows. Please try again.');
        setWorkflows(new Map());
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setErrors({});
    let newErrors: { [key: string]: string } = {};

    if (!businessData.legalName.trim()) {
      newErrors.legalName = 'Legal name is required';
    }
    if (!businessData.tradeName.trim()) {
      newErrors.tradeName = 'Trade name is required';
    }
    if (!businessData.businessABN.trim()) {
      newErrors.businessABN = 'ABN is required';
    } else if (!/^\d{11}$/.test(businessData.businessABN.replace(/\s/g, ''))) {
      newErrors.businessABN = 'Please enter a valid 11-digit ABN';
    }
    if (!businessData.address.trim()) {
      newErrors.address = 'Address is required';
    }
    if (!businessData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!businessData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    if (!businessData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(businessData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!businessData.contact.trim()) {
      newErrors.phone = 'Phone number is required';
    } else {
      if (!BVTConstants.PHONE_VALIDATION_PATTERN.test(businessData.contact.trim())) {
        newErrors.phone = 'Please enter a valid phone number';
      }
      if (businessData.contact.trim().length < BVTConstants.PHONE_MIN_LENGTH || 
          businessData.contact.trim().length > BVTConstants.PHONE_MAX_LENGTH) {
        newErrors.phone = `Phone number must be between ${BVTConstants.PHONE_MIN_LENGTH}-${BVTConstants.PHONE_MAX_LENGTH} characters`;
      }
    }
    if (!businessData.agentCompanyId) {
      newErrors.agentCompanyId = 'Agent is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post("/createBusiness", businessData);
      console.log('Business User Saved Successfully.');
      toast.success("Business User Created Successfully.");
      navigate(-1);
    } catch (error) {
      console.error('Error saving business user.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet><title>BVT Admin - Users</title></Helmet>
      <Stack spacing={3}>
        <form onSubmit={handleSubmit}>
          <Card>
            <CardHeader subheader="Add a New Business" title="New Business" />
            <Divider />
            <CardContent>
              <Grid container spacing={3}>
                <Grid md={4} xs={12}>
                  <FormControl fullWidth required error={!!errors.legalName}>
                    <InputLabel>Legal Name</InputLabel>
                    <OutlinedInput 
                      label="Legal Name" 
                      name="legalName"
                      value={businessData.legalName}
                      onChange={handleInputChange}
                      inputProps={{ maxLength: 100 }}
                    />
                    {errors.legalName && (
                      <FormHelperText error>{errors.legalName}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid md={4} xs={12}>
                  <FormControl fullWidth required error={!!errors.tradeName}>
                    <InputLabel>Trade Name</InputLabel>
                    <OutlinedInput 
                      label="Trade Name" 
                      name="tradeName"
                      value={businessData.tradeName}
                      onChange={handleInputChange}
                      inputProps={{ maxLength: 100 }}
                    />
                    {errors.tradeName && (
                      <FormHelperText error>{errors.tradeName}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid md={4} xs={12}>
                  <FormControl fullWidth required error={!!errors.businessABN}>
                    <InputLabel>ABN</InputLabel>
                    <OutlinedInput 
                      label="ABN" 
                      name="businessABN"
                      value={businessData.businessABN}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '');
                        setBusinessData(prev => ({
                          ...prev,
                          businessABN: value
                        }));
                        if (errors.businessABN) {
                          setErrors(prev => ({ ...prev, businessABN: '' }));
                        }
                      }}
                      inputProps={{ maxLength: 11 }}
                      placeholder="11 digits"
                    />
                    {errors.businessABN && (
                      <FormHelperText error>{errors.businessABN}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid md={12} xs={12}>
                  <FormControl fullWidth required error={!!errors.address}>
                    <InputLabel>Address</InputLabel>
                    <OutlinedInput 
                      label="Address" 
                      name="address"
                      value={businessData.address}
                      onChange={handleInputChange}
                      inputProps={{ maxLength: 150 }}
                    />
                    {errors.address && (
                      <FormHelperText error>{errors.address}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid md={6} xs={12}>
                  <FormControl fullWidth required error={!!errors.firstName}>
                    <InputLabel>Contact Person First name</InputLabel>
                    <OutlinedInput 
                      label="Contact Person First name" 
                      name="firstName"
                      value={businessData.firstName}
                      onChange={handleInputChange}
                      inputProps={{ maxLength: 25 }}
                    />
                    {errors.firstName && (
                      <FormHelperText error>{errors.firstName}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid md={6} xs={12}>
                  <FormControl fullWidth required error={!!errors.lastName}>
                    <InputLabel>Contact Person Last name</InputLabel>
                    <OutlinedInput 
                      label="Contact Person Last name" 
                      name="lastName"
                      value={businessData.lastName}
                      onChange={handleInputChange}
                      inputProps={{ maxLength: 25 }}
                    />
                    {errors.lastName && (
                      <FormHelperText error>{errors.lastName}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid md={6} xs={12}>
                  <FormControl fullWidth required error={!!errors.email}>
                    <InputLabel>Email address</InputLabel>
                    <OutlinedInput 
                      label="Email address" 
                      name="email"
                      value={businessData.email}
                      onChange={handleInputChange}
                      inputProps={{ maxLength: 50 }}
                    />
                    {errors.email && (
                      <FormHelperText error>{errors.email}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid md={6} xs={12}>
                  <FormControl fullWidth required error={!!errors.phone}>
                    <InputLabel>Phone number</InputLabel>
                    <OutlinedInput 
                      label="Phone number" 
                      name="contact"
                      type="tel"
                      value={businessData.contact}
                      onChange={(e) => {
                        const value = e.target.value.replace(BVTConstants.PHONE_INPUT_PATTERN, '');
                        setBusinessData(prev => ({
                          ...prev,
                          contact: value
                        }));
                        if (errors.phone) {
                          setErrors(prev => ({ ...prev, phone: '' }));
                        }
                      }}
                      inputProps={{ maxLength: BVTConstants.PHONE_MAX_LENGTH }}
                      placeholder="10-15 digits"
                    />
                    {errors.phone && (
                      <FormHelperText error>{errors.phone}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid md={12} xs={12}>
                  <FormControl fullWidth required error={!!errors.agentCompanyId}>
                    <InputLabel>Agent</InputLabel>
                    <Select 
                      label="Agent" 
                      name="agentCompanyId"
                      value={businessData.agentCompanyId.toString()}
                      onChange={handleSelectChange}
                    >
                      {companies.map((option) => (
                        <MenuItem key={option?.companyId} value={option?.companyId}>
                          {option?.companyName}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.agentCompanyId && (
                      <FormHelperText error>{errors.agentCompanyId}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
            <Divider />
            <CardActions sx={{ justifyContent: 'flex-end' }}>
              <Button variant="contained" color='error' onClick={() => navigate(-1)}>Cancel</Button>
              <Button variant="contained" type="submit">Create</Button>
            </CardActions>
          </Card>
        </form>
      </Stack>
      <LoadingSpinner loading={loading} />
    </>
  )
}

export default AdminNewUser